import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, CheckCircle, Target, TrendingUp, Users, Calendar, MapPin, ExternalLink,
  Award, Activity, Heart, BarChart3, AlertCircle, Zap, ArrowRight, TrendingDown,
  Star, Shield, Package, Building, Fingerprint, UserCheck, Phone, MessageCircle,
  Milestone, Utensils, Wheat, Home, Scan, Database, Truck, ClipboardCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic rendering
const iconMap = {
  Users, CheckCircle, Target, TrendingUp, Calendar, MapPin, Award, Activity,
  Heart, BarChart3, AlertCircle, Zap, ArrowRight, TrendingDown, Star, Shield,
  Package, Building, Fingerprint, UserCheck, Phone, MessageCircle, Milestone,
  Utensils, Wheat, Home, Scan, Database, Truck, ClipboardCheck
};

const WCDProgramPageEnhanced = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchProgram();
  }, [slug]);

  const fetchProgram = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/wcd-programs/${slug}`);
      setProgram(response.data);
    } catch (error) {
      console.error('Error fetching program:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName] || Activity;
    return <Icon className="h-5 w-5" />;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading program details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Program Not Found</h2>
              <p className="text-gray-600 mb-4">The requested WCD program could not be found.</p>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Programs
          </button>

          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 mb-8 text-white">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm mb-4">
                  {program.category}
                </div>
                <h1 className="text-4xl font-bold mb-4">{program.title}</h1>
                <p className="text-xl text-white/90 mb-6">{program.description}</p>
                
                {program.dashboard_link && (
                  <button
                    onClick={() => navigate(program.dashboard_link)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition"
                  >
                    <BarChart3 className="h-5 w-5" />
                    View Full Dashboard
                    <ExternalLink className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {program.is_active && (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-500 rounded-lg">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-semibold">Active</span>
                </div>
              )}
            </div>
          </div>

          {/* Key Metrics Cards */}
          {program.overview?.key_metrics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {program.overview.key_metrics.map((metric, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{metric.label}</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{metric.value}</p>
                      </div>
                      {metric.icon && (
                        <div className="text-blue-600">
                          {getIcon(metric.icon)}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="success">Success Stories</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="insights">Insights</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Program Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {program.overview?.summary && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Summary</h3>
                      <p className="text-gray-700 leading-relaxed">{program.overview.summary}</p>
                    </div>
                  )}

                  {program.overview?.objectives && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Key Objectives</h3>
                      <ul className="space-y-2">
                        {program.overview.objectives.map((obj, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{obj}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {program.overview?.target_beneficiaries && (
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="h-5 w-5 text-blue-600" />
                          <h3 className="font-semibold text-gray-900">Target Beneficiaries</h3>
                        </div>
                        <p className="text-gray-700">{program.overview.target_beneficiaries}</p>
                      </div>
                    )}

                    {program.overview?.geographic_coverage && (
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="h-5 w-5 text-purple-600" />
                          <h3 className="font-semibold text-gray-900">Geographic Coverage</h3>
                        </div>
                        <p className="text-gray-700">{program.overview.geographic_coverage}</p>
                      </div>
                    )}

                    {program.overview?.timeline && (
                      <div className="p-4 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="h-5 w-5 text-green-600" />
                          <h3 className="font-semibold text-gray-900">Timeline</h3>
                        </div>
                        <p className="text-gray-700">{program.overview.timeline}</p>
                      </div>
                    )}

                    {program.overview?.status && (
                      <div className="p-4 bg-yellow-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="h-5 w-5 text-yellow-600" />
                          <h3 className="font-semibold text-gray-900">Status</h3>
                        </div>
                        <p className="text-gray-700">{program.overview.status}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Features Tab */}
            <TabsContent value="features" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {program.features && program.features.map((feature, index) => (
                  <Card key={index} className="hover:shadow-lg transition">
                    <CardHeader>
                      <div className="flex items-start gap-3">
                        {feature.icon && (
                          <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                            {getIcon(feature.icon)}
                          </div>
                        )}
                        <div className="flex-1">
                          <CardTitle className="text-lg">
                            {feature.title || feature.name}
                          </CardTitle>
                          <CardDescription className="mt-2">
                            {feature.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    {feature.benefits && (
                      <CardContent>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Key Benefits:</h4>
                        <ul className="space-y-1">
                          {feature.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Success Stories Tab */}
            <TabsContent value="success" className="space-y-6">
              {program.success_stories && program.success_stories.length > 0 ? (
                <div className="grid grid-cols-1 gap-6">
                  {program.success_stories.map((story, index) => (
                    <Card key={index} className="border-l-4 border-green-500">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-xl flex items-center gap-2">
                              <Award className="h-6 w-6 text-green-600" />
                              {story.title}
                            </CardTitle>
                            {story.location && (
                              <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {story.location}
                              </p>
                            )}
                          </div>
                          {story.date && (
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {story.date}
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {story.achievement && (
                          <div className="p-4 bg-green-50 rounded-lg">
                            <h4 className="font-semibold text-green-900 mb-2">Achievement</h4>
                            <p className="text-green-800">{story.achievement}</p>
                          </div>
                        )}
                        
                        {story.description && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Story</h4>
                            <p className="text-gray-700 leading-relaxed">{story.description}</p>
                          </div>
                        )}
                        
                        {story.impact && (
                          <div className="p-4 bg-blue-50 rounded-lg">
                            <h4 className="font-semibold text-blue-900 mb-2">Impact</h4>
                            <p className="text-blue-800 font-medium">{story.impact}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Success stories coming soon...</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-6">
              {program.analytics_preview ? (
                <>
                  {/* KPI Cards */}
                  {program.analytics_preview.kpis && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {program.analytics_preview.kpis.map((kpi, index) => (
                        <Card key={index}>
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-600">{kpi.metric}</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                  {kpi.value}{kpi.unit}
                                </p>
                              </div>
                              {kpi.trend && getTrendIcon(kpi.trend)}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Dashboard Link */}
                  {program.dashboard_link && (
                    <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                      <CardContent className="py-8">
                        <div className="text-center">
                          <BarChart3 className="h-12 w-12 mx-auto mb-4" />
                          <h3 className="text-2xl font-bold mb-2">View Full Dashboard</h3>
                          <p className="mb-6 text-white/90">
                            Access comprehensive analytics, real-time data, and interactive visualizations
                          </p>
                          <button
                            onClick={() => navigate(program.dashboard_link)}
                            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition"
                          >
                            Open Dashboard
                            <ExternalLink className="h-5 w-5" />
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {program.analytics_preview.charts && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Available Visualizations</CardTitle>
                        <CardDescription>Interactive charts and analysis tools in the full dashboard</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {program.analytics_preview.charts.map((chart, index) => (
                            <div key={index} className="p-4 border rounded-lg hover:border-blue-600 transition">
                              <BarChart3 className="h-8 w-8 text-blue-600 mb-2" />
                              <p className="font-medium text-gray-900 capitalize">
                                {chart.replace(/_/g, ' ')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">Analytics dashboard not available yet</p>
                    {program.dashboard_link && (
                      <button
                        onClick={() => navigate(program.dashboard_link)}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        View Dashboard
                      </button>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Insights Tab */}
            <TabsContent value="insights" className="space-y-6">
              {program.insights ? (
                <>
                  {/* Summary */}
                  <Card className="border-l-4 border-blue-600">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Zap className="h-6 w-6 text-blue-600" />
                        Key Insights Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700 text-lg leading-relaxed">{program.insights.summary}</p>
                    </CardContent>
                  </Card>

                  {/* Achievements */}
                  {program.insights.achievements && program.insights.achievements.length > 0 && (
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Award className="h-7 w-7 text-green-600" />
                        Key Achievements
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {program.insights.achievements.map((achievement, index) => (
                          <Card key={index} className="border-l-4 border-green-500">
                            <CardHeader>
                              <CardTitle className="text-lg text-green-900">{achievement.title}</CardTitle>
                              <div className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold mt-2">
                                {achievement.metric}
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              <p className="text-gray-700">{achievement.description}</p>
                              <div className="p-3 bg-blue-50 rounded-lg">
                                <p className="text-sm font-semibold text-blue-900 mb-1">Recommendation:</p>
                                <p className="text-sm text-blue-800">{achievement.recommendation}</p>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Improvements Needed */}
                  {program.insights.improvements_needed && program.insights.improvements_needed.length > 0 && (
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <AlertCircle className="h-7 w-7 text-orange-600" />
                        Areas for Improvement
                      </h3>
                      <div className="space-y-4">
                        {program.insights.improvements_needed.map((improvement, index) => (
                          <Card key={index} className="border-l-4 border-orange-500">
                            <CardHeader>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <CardTitle className="text-lg text-orange-900">{improvement.title}</CardTitle>
                                  <div className="inline-block px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold mt-2">
                                    {improvement.metric}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {improvement.priority === 'critical' && (
                                    <span className="px-3 py-1 bg-red-600 text-white rounded-full text-xs font-bold">
                                      CRITICAL
                                    </span>
                                  )}
                                  {improvement.priority === 'high' && (
                                    <span className="px-3 py-1 bg-orange-600 text-white rounded-full text-xs font-bold">
                                      HIGH
                                    </span>
                                  )}
                                  {improvement.priority === 'medium' && (
                                    <span className="px-3 py-1 bg-yellow-600 text-white rounded-full text-xs font-bold">
                                      MEDIUM
                                    </span>
                                  )}
                                  {improvement.priority === 'low' && (
                                    <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-bold">
                                      LOW
                                    </span>
                                  )}
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Challenge:</h4>
                                <p className="text-gray-700">{improvement.description}</p>
                              </div>
                              
                              <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                                <h4 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
                                  <Star className="h-4 w-4" />
                                  Recommendation:
                                </h4>
                                <p className="text-sm text-blue-800">{improvement.recommendation}</p>
                              </div>

                              {improvement.action_items && improvement.action_items.length > 0 && (
                                <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border-l-4 border-purple-600">
                                  <h4 className="text-base font-bold text-purple-900 mb-3 flex items-center gap-2">
                                    <ArrowRight className="h-5 w-5" />
                                    Next Steps / Action Items:
                                  </h4>
                                  <ul className="space-y-2">
                                    {improvement.action_items.map((item, idx) => (
                                      <li key={idx} className="flex items-start gap-3 p-2 bg-white/60 rounded">
                                        <div className="flex-shrink-0 mt-0.5">
                                          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-purple-600 text-white text-xs font-bold">
                                            {idx + 1}
                                          </div>
                                        </div>
                                        <span className="text-sm text-purple-900 font-medium">{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Trends */}
                  {program.insights.key_trends && program.insights.key_trends.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TrendingUp className="h-6 w-6 text-blue-600" />
                          Key Trends
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {program.insights.key_trends.map((trend, index) => (
                            <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                              {trend.direction === 'positive' && (
                                <TrendingUp className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                              )}
                              {trend.direction === 'negative' && (
                                <TrendingDown className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                              )}
                              {trend.direction === 'neutral' && (
                                <ArrowRight className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
                              )}
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">{trend.trend}</p>
                                <p className="text-sm text-gray-600 mt-1">{trend.data}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Predicted Outcomes */}
                  {program.insights.predicted_outcomes && (
                    <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Target className="h-6 w-6" />
                          Predicted Outcomes
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="p-4 bg-white/10 rounded-lg backdrop-blur">
                          <h4 className="font-semibold mb-2 flex items-center gap-2">
                            <AlertCircle className="h-5 w-5" />
                            If No Action Taken:
                          </h4>
                          <p className="text-white/90">{program.insights.predicted_outcomes.if_no_action}</p>
                        </div>
                        <div className="p-4 bg-white/10 rounded-lg backdrop-blur">
                          <h4 className="font-semibold mb-2 flex items-center gap-2">
                            <CheckCircle className="h-5 w-5" />
                            If Action Taken:
                          </h4>
                          <p className="text-white/90">{program.insights.predicted_outcomes.if_action_taken}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Insights not available yet</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default WCDProgramPageEnhanced;
